import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useQueryClient } from 'react-query'
import { ApiInstance } from '@/lib/api'
import {
  EMPTY_USER,
  clearSession,
  getAccessToken,
  getCompanyId,
  onSessionEnded,
  readStoredUser,
  setCompanyId,
  storeUser,
  type SessionUser
} from '@/lib/session'

export type { SessionUser } from '@/lib/session'

export interface SessionCompany {
  id: string
  name: string
}

export interface AuthContextType {
  user: SessionUser
  company: SessionCompany
  signIn: (user: SessionUser, company: SessionCompany, callback: VoidFunction) => void
  signOut: (callback: VoidFunction) => void
  setSelectedCompany: (company: SessionCompany, options?: { silent?: boolean }) => void
  renameCompany: (companyId: string, name: string) => void
}

const EMPTY_COMPANY: SessionCompany = { id: '', name: '' }

export const AuthContext = createContext<AuthContextType>(null!)

export const useAuth = () => useContext(AuthContext)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser>(() => readStoredUser())
  const [company, setCompany] = useState<SessionCompany>(EMPTY_COMPANY)
  const queryClient = useQueryClient()
  const signedInRef = useRef(!!user.id)
  signedInRef.current = !!user.id

  useEffect(() => {
    const reset = () => {
      setUser(EMPTY_USER)
      setCompany(EMPTY_COMPANY)
      queryClient.clear()
    }
    const unsubscribe = onSessionEnded(reset)
    const checkSession = () => {
      if (document.visibilityState === 'visible' && signedInRef.current && !getAccessToken()) reset()
    }
    document.addEventListener('visibilitychange', checkSession)
    return () => {
      unsubscribe()
      document.removeEventListener('visibilitychange', checkSession)
    }
  }, [queryClient])

  const signIn = (nextUser: SessionUser, nextCompany: SessionCompany, callback: VoidFunction) => {
    setUser(nextUser)
    storeUser(nextUser)
    setCompany(nextCompany)
    if (nextCompany.id) setCompanyId(nextCompany.id)
    callback()
  }

  const signOut = (callback: VoidFunction) => {
    const token = getAccessToken()
    if (token) ApiInstance.post('/auth/logout', null, { headers: { Authorization: `Bearer ${token}` } }).catch(() => undefined)

    clearSession()
    setUser(EMPTY_USER)
    setCompany(EMPTY_COMPANY)
    queryClient.removeQueries()
    callback()
  }

  const setSelectedCompany = (next: SessionCompany, options?: { silent?: boolean }) => {
    queryClient.resetQueries()
    const previousId = getCompanyId()
    setCompanyId(next.id)
    setCompany({ id: next.id, name: next.name })
    if (previousId !== next.id && !options?.silent) {
      toast.success('Imobiliária selecionada', {
        description: (
          <>
            Você está trabalhando em <span className="font-semibold text-foreground">{next.name}</span>.
          </>
        )
      })
    }
  }

  const renameCompany = (companyId: string, name: string) => setCompany(current => (current.id === companyId ? { ...current, name } : current))

  const value = { user, company, signIn, signOut, setSelectedCompany, renameCompany }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
